import { useState } from 'react';
import { UserPlus, X, Check, Gift, Copy, Share2 } from 'lucide-react';
import type { GuestPass } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Modal } from '@techsofit/ui';
import { useAsync } from '../lib/useAsync';
import { getMyGuestPasses, inviteGuest } from '../lib/memberApi';
import { formatDMY, parseDate, today, daysBetween } from '../lib/format';
import { copyText, shareText } from '../lib/share';
import { isIntlPhone, normalizeIntlPhone, INTL_PHONE_PLACEHOLDER } from '../lib/phone';

/** "01/07/2026 (in 8 days)" — dd/mm/yyyy plus a friendly countdown. */
function whenAvailable(iso: string): string {
  const days = daysBetween(today(), parseDate(iso));
  const rel = days <= 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;
  return `${formatDMY(iso)} (${rel})`;
}

/** Member "bring a friend" card + modal. Renders only when the gym has guest passes on. */
export function InviteFriend({ gymName }: { gymName?: string | null }) {
  const { data, loading, reload } = useAsync(getMyGuestPasses);
  const [open, setOpen] = useState(false);

  if (loading || !data?.enabled) return null;

  const canInvite = data.remaining > 0;
  return (
    <>
      <Card className="mb-5 flex items-center gap-3 p-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-control bg-accent text-accent-ink">
          <Gift className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-ink">Bring a friend</p>
          <p className="text-xs text-muted">
            {canInvite
              ? `You have ${data.remaining} free guest pass${data.remaining === 1 ? '' : 'es'} this month.`
              : data.nextAvailable
                ? `Used up. Your next pass is available ${whenAvailable(data.nextAvailable)}.`
                : 'No passes available right now.'}
          </p>
        </div>
        <Button size="sm" disabled={!canInvite} onClick={() => setOpen(true)}>
          <UserPlus className="size-4" aria-hidden /> Invite
        </Button>
      </Card>

      {open && (
        <InviteModal
          gymName={gymName}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            reload();
          }}
        />
      )}
    </>
  );
}

function InviteModal({ gymName, onClose, onSaved }: { gymName?: string | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<GuestPass | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const shareMessage = created
    ? `You've got a free guest pass to ${gymName ?? 'my gym'}! Show this code at the front desk: ${created.code} (valid until ${formatDMY(created.expiresAt)}).`
    : '';

  async function copyCode() {
    if (!created) return;
    setNote((await copyText(created.code)) ? 'Code copied' : 'Could not copy');
  }

  async function share() {
    if (!created) return;
    const r = await shareText(shareMessage, 'Guest pass');
    if (r === 'copied') setNote('Invite copied — paste it to your friend');
    else if (r === 'failed') setNote('Could not share');
  }

  async function save() {
    setError(null);
    if (!name.trim()) {
      setError("Enter your friend's name.");
      return;
    }
    if (phone.trim() && !isIntlPhone(phone)) {
      setError('Enter the phone in international format, e.g. +263 77 123 4567.');
      return;
    }
    setSaving(true);
    try {
      const pass = await inviteGuest({
        guestName: name.trim(),
        guestEmail: email.trim() || null,
        guestPhone: phone.trim() ? normalizeIntlPhone(phone) : null,
      });
      setCreated(pass);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not create the pass.');
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={created ? onSaved : onClose}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Invite a friend</h2>
          <button type="button" aria-label="Close" onClick={created ? onSaved : onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>

        {created ? (
          <div className="flex flex-col items-center gap-2 py-2 text-center">
            <span className="grid size-12 place-items-center rounded-full bg-ok-soft text-ok-ink">
              <Check className="size-6" aria-hidden />
            </span>
            <p className="text-sm text-muted">{created.guestName}'s guest pass</p>
            <p className="font-display text-3xl font-bold tracking-[0.2em] text-ink">{created.code}</p>
            <p className="text-sm text-muted">
              {created.guestEmail || created.guestPhone ? "We've sent it to them. " : ''}
              Share this code, they show it at the front desk. Valid until {formatDMY(created.expiresAt)}.
            </p>

            <div className="mt-2 flex w-full gap-2">
              <Button variant="outline" className="flex-1" onClick={copyCode}>
                <Copy className="size-4" aria-hidden /> Copy code
              </Button>
              <Button className="flex-1" onClick={share}>
                <Share2 className="size-4" aria-hidden /> Share
              </Button>
            </div>
            {note && <p className="text-xs font-medium text-ok-ink">{note}</p>}

            <button type="button" onClick={onSaved} className="mt-1 text-sm font-medium text-muted hover:text-ink">
              Done
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted">They get a free visit. Give us their details and we'll create a pass code.</p>
            <Input label="Friend's name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <Input label="Their email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} hint="If set, we'll email them the pass." />
            <Input
              label="Their phone (optional)"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={INTL_PHONE_PLACEHOLDER}
              hint="International format so we can text them the pass."
            />
            {error && (
              <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
                {error}
              </p>
            )}
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button onClick={save} loading={saving}>{!saving && <Check className="size-4" aria-hidden />} Create pass</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
